import {cva} from 'class-variance-authority';
import clsx from 'clsx';
import * as React from 'react';
import {Link, useMatch} from 'react-router-dom';

const menuItemClass = cva(
  [
    'group relative gap-2 flex min-w-0 flex-1 cursor-pointer flex-row items-center rounded-md px-3 py-1 w-full',
    'text-sidebar-foreground/70',
    'hover:bg-sidebar-accent has-[.action:hover]:hover:bg-transparent',
    '[&>svg]:size-4 [&>svg]:shrink-0',
  ],
  {
    variants: {
      isActive: {
        true: 'bg-sidebar-accent text-sidebar-accent-foreground font-normal',
        false: 'font-light',
      },
      isDragging: {
        true: 'border-sidebar-border bg-sidebar-accent/50 border',
        false: '',
      },
      level: {
        primary: 'text-md my-2',
        secondary: 'text-sm',
      },
    },
  },
);

type MenuItemContentProps = {
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  label: React.ReactNode;
};
const MenuItemContent = ({
  iconLeft,
  iconRight,
  label,
}: MenuItemContentProps) => {
  return (
    <>
      {iconLeft || null}
      <div className="min-w-0 flex-1">
        <span className="block truncate text-left">{label}</span>
      </div>
      <div
        className={clsx(
          'action',
          '-my-1 flex flex-row items-center',
          'pointer-events-none opacity-0',
          'group-hover:pointer-events-auto group-hover:relative group-hover:opacity-100',
        )}
      >
        {iconRight}
      </div>
    </>
  );
};

type MenuLinkProps = {
  path: string;
  queryString?: string;
  isActive?: boolean;
  isDragging?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  label?: React.ReactNode;
  level?: 'primary' | 'secondary';
  onClick?: () => void;
};
export const MenuLink = ({
  path,
  queryString,
  isActive,
  isDragging,
  iconLeft,
  iconRight,
  label,
  level,
  onClick,
}: MenuLinkProps) => {
  const match = !!useMatch({path, end: true});

  const to = {
    pathname: path,
    search: queryString,
  };

  const className = menuItemClass({
    isActive: isActive ?? match,
    isDragging,
    level: level ?? 'primary',
  });

  return (
    <Link to={to} className={className} onClick={onClick}>
      <MenuItemContent
        iconLeft={iconLeft}
        iconRight={iconRight}
        label={label}
      />
    </Link>
  );
};

type MenuButtonProps = {
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  label?: React.ReactNode;
} & React.ComponentProps<'button'>;
export const MenuButton = ({
  iconLeft,
  iconRight,
  label,
  ...rest
}: MenuButtonProps) => {
  const className = menuItemClass({
    isActive: false,
    isDragging: false,
    level: 'primary',
  });

  return (
    <button {...rest} className={className}>
      <MenuItemContent
        iconLeft={iconLeft}
        iconRight={iconRight}
        label={label}
      />
    </button>
  );
};
